import React from "react";

//App
import { OrganizationCampaignDto } from "../../../app/model/api/types/dto/organizationCampaignDto";
import reportApi from "../../../app/model/api/reportApi";

//Common
import {
  DataGrid,
  ConfirmationDialog,
  Snackbar,
  Drawer,
  Chip,
} from "../../common/VFX-UI";
import { MoreActionButton } from "../../common/Buttons";
import PageHeader from "../../common/PageHeader/PageHeader";
import MoreActionsMenu from "../../common/MoreActionsMenu/MoreActionsMenu";
import SearchContainer from "../../common/Forms/SearchContainer/SearchContainer";
import { SearchProps } from "../../../types";
import { SearchDefaultValue } from "../../../store/types";
import { SearchFieldsDefaultValue } from "../../../utility/constants/constants";
import { CampaignProps } from "../../../types/campaign";
import useCampaignStyles from "../../Campaign/styles";
import CampaignView from "../../Campaign/CampaignView";
import ExportButton from "../../common/Buttons/ExportButton";
import useCampaign from "./hooks/useCampaign";

const Campaigns: React.FC<CampaignProps> = (props) => {
  const campaignStyles = useCampaignStyles();

  const { data, activateMutation, deleteMutation, searchValue } = props;
  const {
    state,
    formContext,
    rowEvents,
    actionColumnAnchorEl,
    toggleViewHandler,
    toggleEditViewHandler,
    toggleActivateHandler,
    toggleDeactivateHandler,
    handleDeactivate,
    handleActivate,
    onAddSnackbarClose,
    handleActionClick,
    setActionColumnAnchorEl,
  } = useCampaign({
    deleteMutation: deleteMutation,
    activateMutation: activateMutation,
  });

  const openMenu = Boolean(actionColumnAnchorEl);
  const searchProps: SearchProps<OrganizationCampaignDto> = {
    searchValue: formContext?.searchValue ?? SearchDefaultValue,
    searchFields: { ...SearchFieldsDefaultValue, name: true },
    setSearchValue: formContext?.setSearchValue,
    refetchGrid: formContext?.refetchGrid,
  };

  const columns = [
    {
      dataField: "id",
      text: "ID",
      hidden: true,
    },
    {
      dataField: "display",
      text: "Name",
      sort: true,
    },
    {
      dataField: "description",
      text: "Description",
      sort: true,
    },
    {
      dataField: "code",
      text: "Code",
      sort: false,
      hidden: true,
    },
    {
      dataField: "organization",
      text: "Organization",
      sort: true,
      hidden: true,
    },
    {
      dataField: "isActive",
      text: "Status",
      sort: false,
      formatter: (cell: boolean, row: any) => {
        if (row.isCompleted) return <Chip label={"Completed"} />;
        else return <Chip label={cell ? "Active" : "Inactive"} />;
      },
    },
    {
      dataField: "action",
        text: "",
        style: { width: "25px" },
      isDummyField: true,
      formatter: () => {
        return (
          <MoreActionButton handleActionClick={(e) => handleActionClick(e)} />
        );
      },
    },
  ];

  const exportUrl = reportApi.exportCampaigns({
    name: searchValue,
    isActive: true,
    pageNumber: -1,
    pageSize: -1,
    sortBy: [],
  });

  return (
    <>
      <>
        <PageHeader>Campaigns</PageHeader>
        <div style={campaignStyles.searchCampaignContainer}>
          <SearchContainer searchProps={searchProps} />
          {(data?.length!==0) && data &&
            <div className="mb-3" style={campaignStyles.completedButtonContainer}>
                <ExportButton method="GET" url={exportUrl} fileName="Campaigns"/>
            </div>
          }
        </div>
      </>
      <Drawer show={state.toggleView}>
        {state.toggleView && formContext?.data && (
          <CampaignView
            onClose={toggleViewHandler}
            editAction={() => {
              toggleEditViewHandler();
            }}
            campaignData={formContext?.data}
            toggleDeactivateHandler={toggleDeactivateHandler}
            toggleActivateHandler={toggleActivateHandler}
          />
        )}
      </Drawer>

      <DataGrid
        columns={columns}
        data={data}
        keyField="id"
        rowEvents={rowEvents}
        totalSize={formContext?.paging?.totalSize}
        remote={true}
        page={formContext?.paging?.currentPage}
        pageSize={formContext?.pageSize}
        handlePageChange={async (newPage, pageSize, sortField, sortOrder) => {
          if (formContext?.setCurrentPage && newPage) {
            await formContext?.setCurrentPage(newPage);
          }
          if (formContext?.setCurrentPageSize && pageSize) {
            await formContext?.setCurrentPageSize(pageSize);
          }
          if (formContext?.setSortBy && sortField && sortOrder) {
            await formContext?.setSortBy({
              field: sortField,
              direction: sortOrder === "asc" ? 1 : -1,
            });
          }
          formContext?.refetchGrid();
        }}
      />

      {actionColumnAnchorEl && (
        <MoreActionsMenu
          isOpen={openMenu}
          target={actionColumnAnchorEl}
          toggle={() => {
            setActionColumnAnchorEl(null);
          }}
          onViewClick={() => {
            toggleViewHandler();
            setActionColumnAnchorEl(null);
          }}
        />
      )}

      <ConfirmationDialog
        open={state.isDeactivateDialogOpen}
        onClose={toggleDeactivateHandler}
        onConfirm={handleDeactivate}
        contentTitle={`Deactivate ${formContext?.data?.name}`}
        contentText={"Are you sure you want to continue?"}
        confirmText={"Yes, Continue"}
        closeText={"No, Go Back"}
      ></ConfirmationDialog>

      <ConfirmationDialog
        open={state.isActivateDialogOpen}
        onClose={toggleActivateHandler}
        onConfirm={handleActivate}
        contentTitle={`Activate ${formContext?.data?.name}`}
        contentText={"Are you sure you want to continue?"}
        confirmText={"Yes, Continue"}
        closeText={"No, Go Back"}
      />

      {state.showSuccessSnackbar && (
        <Snackbar
          open={state.showSuccessSnackbar}
          message={state.successMessage}
          handleClose={onAddSnackbarClose}
          severity="success"
        />
      )}
    </>
  );
};

export default Campaigns;
